/**
 * useCalendar Hook
 *
 * React Query hooks for calendar data management:
 * - useCalendarView: Fetch calendar data for a date range
 * - useSchedulePost: Schedule a post for publishing
 * - useReschedulePost: Reschedule an existing post
 * - useUnschedulePost: Remove a post from schedule
 * - useBestTimeSuggestions: Get AI-driven best time suggestions
 * - useCalendarConflicts: Get scheduling conflicts
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCalendarView,
  schedulePost,
  reschedulePost,
  unschedulePost,
  getBestTimeSuggestions,
  getConflicts,
  type CalendarQueryParams,
  type CalendarViewResponse,
  type SchedulePostRequest,
  type SchedulePostResponse,
  type ReschedulePostRequest,
  type PostSchedule,
  type UnscheduleResponse,
  type BestTimeSuggestionsQuery,
  type BestTimeSuggestionsResponse,
  type ConflictsQueryParams,
  type ConflictsResponse,
} from '../lib/api';

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Query keys for calendar-related queries
 * Using a factory pattern for consistent key generation
 */
export const calendarKeys = {
  all: ['calendar'] as const,
  views: () => [...calendarKeys.all, 'view'] as const,
  view: (params: CalendarQueryParams) => [...calendarKeys.views(), params] as const,
  suggestions: () => [...calendarKeys.all, 'suggestions'] as const,
  suggestion: (params?: BestTimeSuggestionsQuery) => [...calendarKeys.suggestions(), params] as const,
  conflicts: () => [...calendarKeys.all, 'conflicts'] as const,
  conflict: (params?: ConflictsQueryParams) => [...calendarKeys.conflicts(), params] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Hook to fetch calendar view data for a date range
 *
 * @param params - Calendar query parameters (startDate, endDate, view, timezone, platform)
 * @param options - Additional React Query options
 *
 * @example
 * const { data, isLoading, error } = useCalendarView({
 *   startDate: '2025-01-01',
 *   endDate: '2025-01-07',
 *   view: 'week',
 *   timezone: 'America/New_York',
 * });
 */
export function useCalendarView(
  params: CalendarQueryParams,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
    staleTime?: number;
  }
) {
  return useQuery<CalendarViewResponse, Error>({
    queryKey: calendarKeys.view(params),
    queryFn: () => getCalendarView(params),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
    staleTime: options?.staleTime ?? 30000, // 30 seconds default stale time
  });
}

/**
 * Hook to fetch AI-driven best time suggestions
 *
 * @param params - Query parameters (platform, limit)
 * @param options - Additional React Query options
 *
 * @example
 * const { data, isLoading } = useBestTimeSuggestions({ platform: 'linkedin', limit: 5 });
 */
export function useBestTimeSuggestions(
  params?: BestTimeSuggestionsQuery,
  options?: {
    enabled?: boolean;
    staleTime?: number;
  }
) {
  return useQuery<BestTimeSuggestionsResponse, Error>({
    queryKey: calendarKeys.suggestion(params),
    queryFn: () => getBestTimeSuggestions(params),
    enabled: options?.enabled ?? true,
    staleTime: options?.staleTime ?? 300000, // 5 minutes - suggestions don't change often
  });
}

/**
 * Hook to fetch scheduling conflicts
 *
 * @param params - Query parameters (startDate, endDate, resolved)
 * @param options - Additional React Query options
 *
 * @example
 * const { data, isLoading } = useCalendarConflicts({ resolved: false });
 */
export function useCalendarConflicts(
  params?: ConflictsQueryParams,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
  }
) {
  return useQuery<ConflictsResponse, Error>({
    queryKey: calendarKeys.conflict(params),
    queryFn: () => getConflicts(params),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
    staleTime: 60000, // 1 minute
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Hook to schedule a post for publishing
 *
 * @example
 * const { mutate: schedule, isPending } = useSchedulePost();
 *
 * schedule({
 *   postId: 'uuid',
 *   scheduledAt: '2025-01-15T10:00:00Z',
 *   timezone: 'America/New_York',
 *   autoPublish: true,
 * }, {
 *   onSuccess: (data) => {
 *     toast.success('Post scheduled successfully');
 *   },
 *   onError: (error) => {
 *     toast.error('Failed to schedule post: ' + error.message);
 *   },
 * });
 */
export function useSchedulePost() {
  const queryClient = useQueryClient();

  return useMutation<SchedulePostResponse, Error, SchedulePostRequest>({
    mutationFn: (data) => schedulePost(data),
    onSuccess: () => {
      // Invalidate calendar views to refetch with new scheduled post
      queryClient.invalidateQueries({ queryKey: calendarKeys.views() });
      // Invalidate conflicts as scheduling may create new conflicts
      queryClient.invalidateQueries({ queryKey: calendarKeys.conflicts() });
    },
  });
}

/**
 * Hook to reschedule an existing scheduled post
 *
 * @example
 * const { mutate: reschedule, isPending } = useReschedulePost();
 *
 * reschedule({
 *   postId: 'uuid',
 *   scheduledAt: '2025-01-16T14:00:00Z',
 *   timezone: 'America/New_York',
 * }, {
 *   onSuccess: (data) => {
 *     toast.success('Post rescheduled successfully');
 *   },
 * });
 */
export function useReschedulePost() {
  const queryClient = useQueryClient();

  return useMutation<
    PostSchedule,
    Error,
    { postId: string } & ReschedulePostRequest
  >({
    mutationFn: ({ postId, ...data }) => reschedulePost(postId, data),
    onSuccess: () => {
      // Invalidate calendar views to refetch with updated schedule
      queryClient.invalidateQueries({ queryKey: calendarKeys.views() });
      // Invalidate conflicts as rescheduling may resolve or create conflicts
      queryClient.invalidateQueries({ queryKey: calendarKeys.conflicts() });
    },
  });
}

/**
 * Hook to unschedule (remove from schedule) a post
 *
 * @example
 * const { mutate: unschedule, isPending } = useUnschedulePost();
 *
 * unschedule('post-uuid', {
 *   onSuccess: () => {
 *     toast.success('Post removed from schedule');
 *   },
 * });
 */
export function useUnschedulePost() {
  const queryClient = useQueryClient();

  return useMutation<UnscheduleResponse, Error, string>({
    mutationFn: (postId) => unschedulePost(postId),
    onSuccess: () => {
      // Invalidate calendar views to refetch without the unscheduled post
      queryClient.invalidateQueries({ queryKey: calendarKeys.views() });
      // Invalidate conflicts as unscheduling may resolve conflicts
      queryClient.invalidateQueries({ queryKey: calendarKeys.conflicts() });
    },
  });
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook that combines calendar view, suggestions, and conflicts
 * Useful for the main calendar page component
 *
 * @param params - Calendar query parameters
 *
 * @example
 * const {
 *   calendarView,
 *   suggestions,
 *   conflicts,
 *   isLoading,
 *   isError,
 * } = useCalendarData({
 *   startDate: '2025-01-01',
 *   endDate: '2025-01-07',
 *   view: 'week',
 * });
 */
export function useCalendarData(params: CalendarQueryParams) {
  const calendarViewQuery = useCalendarView(params);
  const suggestionsQuery = useBestTimeSuggestions();
  const conflictsQuery = useCalendarConflicts({
    startDate: params.startDate,
    endDate: params.endDate,
  });

  return {
    // Calendar view data
    calendarView: calendarViewQuery.data,
    isCalendarLoading: calendarViewQuery.isLoading,
    isCalendarError: calendarViewQuery.isError,
    calendarError: calendarViewQuery.error,
    refetchCalendar: calendarViewQuery.refetch,

    // Best time suggestions
    suggestions: suggestionsQuery.data,
    isSuggestionsLoading: suggestionsQuery.isLoading,

    // Conflicts
    conflicts: conflictsQuery.data,
    isConflictsLoading: conflictsQuery.isLoading,

    // Combined loading state
    isLoading:
      calendarViewQuery.isLoading ||
      suggestionsQuery.isLoading ||
      conflictsQuery.isLoading,

    // Combined error state
    isError: calendarViewQuery.isError,
  };
}

/**
 * Hook to invalidate all calendar-related queries
 * Useful after bulk operations or when workspace changes
 *
 * @example
 * const invalidateCalendar = useInvalidateCalendar();
 * invalidateCalendar(); // Invalidates all calendar queries
 */
export function useInvalidateCalendar() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: calendarKeys.all });
  };
}
